/**
 * Fraîcheur des données : revalidation au focus, jeton /revision, événements locaux.
 * Un seul fetch in-flight (le précédent est annulé). Pas de polling du payload.
 */
import { useEffect, useRef } from 'react'
import { getRevision } from '../services/api'
import { invalidateCache } from '../utils/cache'
import { isAbortError } from '../utils/abort'

export const DATA_CHANGE_EVENTS = [
  'operationChanged',
  'operationsChanged',
  'comptesChanged',
  'paramsChanged',
  'currencyChanged',
  'categoriesChanged',
  'tokenChanged',
]

const POLL_MS = 90 * 1000
const FOCUS_DEBOUNCE_MS = 300

let sharedRevision = null

export function resetDataRevision() {
  sharedRevision = null
}

function invalidateDataCache() {
  ;['/stats', '/operations', '/comptes', '/parametres', '/categories', '/graphiques'].forEach(
    (url) => invalidateCache(url)
  )
}

/**
 * @param {{
 *   onRefresh: (opts: { signal: AbortSignal, forceRefresh: boolean, background: boolean }) => Promise<void>,
 *   enabled?: boolean,
 *   events?: string[],
 * }} options
 */
export function useFreshData({ onRefresh, enabled = true, events = DATA_CHANGE_EVENTS }) {
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh
  const abortRef = useRef(null)
  const eventsKey = events.join(',')

  useEffect(() => {
    if (!enabled) return undefined

    const run = async (reason) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const { signal } = controller

      try {
        if (reason === 'poll' || reason === 'focus') {
          const v = await getRevision({ signal })
          if (signal.aborted) return
          if (sharedRevision !== null && v === sharedRevision) return
          sharedRevision = v
          invalidateDataCache()
          await onRefreshRef.current({ signal, forceRefresh: true, background: true })
          return
        }

        if (reason === 'event') {
          invalidateDataCache()
          await onRefreshRef.current({ signal, forceRefresh: true, background: true })
          if (signal.aborted) return
          sharedRevision = await getRevision({ signal })
          return
        }

        await onRefreshRef.current({ signal, forceRefresh: false, background: false })
        if (signal.aborted) return
        const v = await getRevision({ signal })
        if (signal.aborted) return
        if (sharedRevision !== null && v === sharedRevision) return
        const known = sharedRevision !== null
        sharedRevision = v
        if (known) {
          invalidateDataCache()
          await onRefreshRef.current({ signal, forceRefresh: true, background: true })
        }
      } catch (error) {
        if (isAbortError(error) || signal.aborted) return
        console.error('useFreshData:', error)
      }
    }

    run('mount')

    let focusTimer
    const scheduleFocus = () => {
      if (document.hidden) return
      clearTimeout(focusTimer)
      focusTimer = setTimeout(() => run('focus'), FOCUS_DEBOUNCE_MS)
    }
    const onVisibility = () => {
      if (!document.hidden) scheduleFocus()
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', scheduleFocus)

    const interval = setInterval(() => {
      if (!document.hidden) run('poll')
    }, POLL_MS)

    let eventQueued = false
    const onEvent = () => {
      if (eventQueued) return
      eventQueued = true
      queueMicrotask(() => {
        eventQueued = false
        run('event')
      })
    }
    const eventNames = eventsKey.split(',')
    eventNames.forEach((name) => window.addEventListener(name, onEvent))

    return () => {
      abortRef.current?.abort()
      clearTimeout(focusTimer)
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', scheduleFocus)
      eventNames.forEach((name) => window.removeEventListener(name, onEvent))
    }
  }, [enabled, onRefresh, eventsKey])
}
